// components/providers/confirmation-provider.tsx
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';

export type ConfirmVariant = 'default' | 'destructive';

type ConfirmParams = {
  title: string;
  description?: React.ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  /** Use 'destructive' for delete/danger actions to show a red confirm button */
  confirmVariant?: ConfirmVariant;
};

type ConfirmationContextType = {
  confirm: (params: ConfirmParams) => Promise<boolean>;
};

const ConfirmationContext = createContext<ConfirmationContextType>({
  confirm: () => Promise.resolve(false),
});

export function ConfirmationProvider({ children }: PropsWithChildren) {
  const [dialogState, setDialogState] = useState<ConfirmParams | null>(null);
  const [resolver, setResolver] = useState<(result: boolean) => void>();

  const confirm = useCallback((params: ConfirmParams) => {
    return new Promise<boolean>(resolve => {
      setDialogState(params);
      setResolver(() => resolve);
    });
  }, []);

  const handleClose = () => {
    setDialogState(null);
    resolver?.(false);
  };

  const handleConfirm = () => {
    setDialogState(null);
    resolver?.(true);
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      {dialogState && (
        <ConfirmationDialog
          open
          onOpenChange={open => {
            if (!open) handleClose();
          }}
          title={dialogState.title}
          description={dialogState.description}
          cancelLabel={dialogState.cancelLabel}
          confirmLabel={dialogState.confirmLabel}
          confirmVariant={dialogState.confirmVariant}
          onConfirm={handleConfirm}
        />
      )}
    </ConfirmationContext.Provider>
  );
}

export function useConfirmation() {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error(
      'useConfirmation must be used within a ConfirmationProvider',
    );
  }

  return context;
}
